import React from 'react'
import styled from '@emotion/styled'
import { IconButton } from '@mui/material'
import XIcon from '@mui/icons-material/X'
import {
  Facebook,
  Email,
  LinkedIn,
  ResiliaIconBlue,
} from '../Misc/SVGs'
import { colors } from '../../../tokens/colors'

type Props = {
  /**
   * callback function for clicks on social sharing buttons
   */
  socialClickHandler: (text: string) => void
}

/**
 * StoryFooter Component
 *
 * Display social sharing buttons for the story as well as "powered by Resilia"
 *
 * @param {Props} props - The component props.
 * @returns {JSX.Element} - A React functional component representing the StoryFooter.
 */
const sharingButtons = [
  { text: 'Facebook', icon: <Facebook /> },
  { text: 'Twitter', icon: <XIcon /> },
  { text: 'LinkedIn', icon: <LinkedIn /> },
  { text: 'Email', icon: <Email /> },
  { text: 'Link', icon: null },
]

export const StoryFooter = ({ socialClickHandler }: Props): JSX.Element => (
    <Footer>
      <FooterTop>
        <div>Help Share This Story</div>
        <LogoContainer>
          {sharingButtons.map(({ text, icon }) => (
            <StyledButton
              key={text}
              className={text.toLowerCase()}
              onClick={() => socialClickHandler(text)}
            >
              {icon || (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height='24'
                  viewBox='0 -960 960 960'
                  width='24'
                >
                  <path d='M440-280H280q-83 0-141.5-58.5T80-480q0-83 58.5-141.5T280-680h160v80H280q-50 0-85 35t-35 85q0 50 35 85t85 35h160v80ZM320-440v-80h320v80H320Zm200 160v-80h160q50 0 85-35t35-85q0-50-35-85t-85-35H520v-80h160q83 0 141.5 58.5T880-480q0 83-58.5 141.5T680-280H520Z' />
                </svg>
              )}
            </StyledButton>
          ))}
        </LogoContainer>
      </FooterTop>
      <FooterBottom>
        Powered by <ResiliaIconBlue />
      </FooterBottom>
    </Footer>
  )

const FooterTop = styled('div')`
  color: ${colors.copy.paragraphDark};
  justify-content: center;
  gap: 10px;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`
const FooterBottom = styled('div')`
  color: #68737b;
  display: flex;
  & svg {
    height: 16px;
  }
`

const LogoContainer = styled('div')`
  justify-content: center;
  gap: 8px;
  display: flex;
`

const Footer = styled('footer')`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  position: relative;
  font-weight: 600;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 14px;
  margin-top: auto;
`
const StyledButton = styled(IconButton)`
  border-radius: 50%;
  padding: 4px;
  width: 32px;
  height: 32px;
  font-weight: 700;
  & svg {
    fill: white;
    height: 24px;
    width: 24px;
    padding: 4px;
  }
  &.facebook {
    background-color: #4267b2;
    font-weight: 700;
    &:hover {
      background-color: #2c4577;
    }
  }
  &.linkedin {
    background-color: #2867b2;
    &:hover {
      background-color: #005e8a;
    }
  }
  &.twitter {
    background-color: #000000;
    &:hover {
      background-color: #566066;
    }
  }
  &.email {
    background-color: #003f7e;
    &:hover {
      background-color: #566066;
    }
  }
  &.link {
    font-weight: 700;
    background-color: #003f7e;
    & svg {
      padding: 0px;
    }
    &:hover {
      background-color: #566066;
    }
  }
`
